import * as Yup from "yup";
import { VALID_LANGUAGE_CODES } from '../constants/languageOptions';
import { VALID_UAE_STATES } from '../constants/uaeStates';

// Enhanced regex patterns for better validation
const phoneRegExp = /^\+?[1-9]\d{1,14}$/; // International phone format
const nameRegExp = /^[A-Za-z\s'.-]+$/; // Allow names with spaces, apostrophes, hyphens, dots
const emiratesIdRegExp = /^784\d{12}$/; // UAE Emirates ID: 784 followed by 12 digits
const passportRegExp = /^[A-Z0-9]{6,12}$/; // Passport: 6-12 alphanumeric characters, uppercase
const emailRegExp = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
const numericRegExp = /^\d+(\.\d+)?$/; // Allow decimal numbers
const postalCodeRegExp = /^[A-Za-z0-9\s-]+$/; // Allow alphanumeric postal codes

// Valid emergency contact relationships
const VALID_RELATIONSHIPS = [
  'spouse', 'parent', 'child', 'sibling', 'friend', 'colleague',
  'guardian', 'grandparent', 'grandchild', 'aunt', 'uncle', 'cousin',
  'other family', 'caregiver', 'other'
];

// Valid religion codes
const VALID_RELIGION_CODES = [
  'ANG', 'BAP', 'BMA', 'BTH', 'BUD', 'CAT', 'CHS', 'CHR', 'EPI', 'ERL',
  'GRE', 'HIN', 'HOT', 'JAI', 'JEW', 'JWN', 'LUT', 'MEN', 'MET', 'MOS',
  'MOT', 'MSH', 'MSU', 'NAZ', 'OTH', 'PEN', 'PRE', 'PRO', 'SEV', 'SHN',
  'SIK', 'UNU', 'VAR'
];

// Custom validation messages
const validationMessages = {
  firstName: {
    required: "First name is required",
    min: "First name must be at least 2 characters",
    max: "First name cannot exceed 50 characters",
    matches: "First name can only contain letters, spaces, apostrophes, hyphens, and dots"
  },
  lastName: {
    required: "Last name is required",
    min: "Last name must be at least 2 characters", 
    max: "Last name cannot exceed 50 characters",
    matches: "Last name can only contain letters, spaces, apostrophes, hyphens, and dots"
  },
  phone: {
    matches: "Please enter a valid phone number (e.g., +971501234567)"
  },
  email: {
    email: "Please enter a valid email address",
    matches: "Please enter a valid email format"
  },
  emiratesId: {
    matches: "Emirates ID must be 15 digits starting with 784 (e.g., 784123456789012)"
  },
  passportNumber: {
    matches: "Passport number must be 6-12 alphanumeric characters (e.g., AB1234567)"
  },
  height: {
    min: "Height must be between 30-300 cm",
    max: "Height must be between 30-300 cm",
    typeError: "Height must be a valid number"
  },
  weight: {
    min: "Weight must be between 1-500 kg", 
    max: "Weight must be between 1-500 kg",
    typeError: "Weight must be a valid number"
  }
};

export const addPatientSchema = Yup.object().shape({
  // Personal Information - Required
  first_name: Yup.string()
    .min(2, validationMessages.firstName.min)
    .max(50, validationMessages.firstName.max)
    .matches(nameRegExp, validationMessages.firstName.matches)
    .required(validationMessages.firstName.required),
  
  last_name: Yup.string()
    .min(2, validationMessages.lastName.min)
    .max(50, validationMessages.lastName.max) 
    .matches(nameRegExp, validationMessages.lastName.matches)
    .required(validationMessages.lastName.required),
  
  date_of_birth: Yup.date()
    .max(new Date(), "Date of birth cannot be in the future")
    .min(new Date(1900, 0, 1), "Please enter a valid date of birth")
    .required("Date of birth is required"),
  
  gender: Yup.string()
    .oneOf(['male', 'female', 'other'], 'Please select a valid gender')
    .required("Gender is required"),
  
  religion: Yup.string()
    .oneOf(VALID_RELIGION_CODES, 'Please select a valid religion')
    .required("Religion is required"),
  
  race: Yup.string()
    .max(50, "Race cannot exceed 50 characters")
    .matches(nameRegExp, "Race can only contain letters, spaces, apostrophes, hyphens, and dots")
    .required("Race is required"),

  language: Yup.string()
    .oneOf(VALID_LANGUAGE_CODES, 'Please select a valid language')
    .required("Language is required"),
  
  marital_status: Yup.string()
    .oneOf(['single', 'married', 'divorced', 'widowed', 'separated'], 'Please select a valid marital status')
    .required("Marital status is required"),
  
  nationality: Yup.string()
    .max(100, 'Nationality must be less than 100 characters')
    .matches(nameRegExp, "Nationality can only contain letters, spaces, apostrophes, hyphens, and dots")
    .required("Nationality is required"),
  
  country: Yup.string()
    .max(100, "Country cannot exceed 100 characters")
    .required("Country of residence is required"),
  
  state: Yup.string()
    .oneOf(VALID_UAE_STATES, 'Please select a valid state')
    .required("State is required"),
  
  citizenship: Yup.string()
    .oneOf(['UAE', 'Resident', 'Visitor'], 'Please select a valid citizenship type')
    .required("Citizenship is required"),
  
  city: Yup.string()
    .max(100, "City cannot exceed 100 characters")
    .matches(nameRegExp, "City can only contain letters, spaces, apostrophes, hyphens, and dots")
    .required("City is required"),
  
  // Contact Information - Required
  phone_country_code: Yup.string()
    .required("Country code is required"),
  
  phone_number: Yup.string()
    .matches(/^[0-9]{7,15}$/, "Please enter a valid phone number (7-15 digits)")
    .required("Phone number is required"),
  
  permanent_address: Yup.string()
    .min(10, "Address must be at least 10 characters long")
    .max(200, "Address cannot exceed 200 characters")
    .required("Permanent address is required"),
  
  // Medical Information - Required
  allergy: Yup.string()
    .max(500, "Allergy information cannot exceed 500 characters")
    .required("Allergy information is required (enter 'None' if no known allergies)"),
  
  primary_physician: Yup.string()
    .max(100, "Primary physician name cannot exceed 100 characters")
    .matches(nameRegExp, "Name contains invalid characters")
    .required("Primary physician is required"),
  
  primary_physician_license: Yup.string()
    .max(50, "License number cannot exceed 50 characters")
    .required("Primary physician license number is required"),
  
  // Emergency Contact - Optional but with validation when provided
  emergency_contact_name: Yup.string()
    .max(100, "Emergency contact name cannot exceed 100 characters")
    .matches(nameRegExp, "Emergency contact name can only contain letters, spaces, apostrophes, hyphens, and dots")
    .nullable(),
  
  emergency_contact_country_code: Yup.string()
    .nullable(),
  
  emergency_contact_phone: Yup.string()
    .matches(/^[0-9]{7,15}$/, "Please enter a valid phone number (7-15 digits)")
    .nullable(),
  
  emergency_contact_relationship: Yup.string()
    .oneOf(VALID_RELATIONSHIPS, `Invalid relationship. Must be one of: ${VALID_RELATIONSHIPS.join(', ')}`)
    .nullable(),

  // Identification - Either/or mandatory validation
  emirates_id: Yup.string()
    .transform((value) => value ? value.replace(/[-\s]/g, '') : value) // Strip dashes and spaces for validation
    .trim()
    .test('emirates-format', validationMessages.emiratesId.matches, function(value) {
      return !value || emiratesIdRegExp.test(value);
    })
    .test('either-or-identification', 'Either Emirates ID or Passport Number is required', function(value) {
      const { passport_number } = this.parent;
      // Only show error on emirates_id field when both are empty to avoid duplicate messages
      if ((!value || !value.trim()) && (!passport_number || !passport_number.trim())) {
        return false;
      }
      return true;
    }),
  
  passport_number: Yup.string()
    .trim()
    .max(20, "Passport number cannot exceed 20 characters")
    .test('passport-format', validationMessages.passportNumber.matches, function(value) {
      return !value || passportRegExp.test(value.toUpperCase());
    }),
  
  // Insurance Information - Optional
  insurance_provider: Yup.string()
    .max(100, "Insurance provider cannot exceed 100 characters")
    .nullable(),
  
  insurance_policy_number: Yup.string()
    .max(50, "Policy number cannot exceed 50 characters")
    .nullable(),
  
  insurance_subscriber_name: Yup.string()
    .max(100, "Subscriber name cannot exceed 100 characters")
    .matches(nameRegExp, "Subscriber name can only contain letters, spaces, apostrophes, hyphens, and dots")
    .nullable(),

  // Patient Classification Flags
  is_vip: Yup.boolean()
    .default(false)
    .nullable(),

  is_tourist: Yup.boolean()
    .default(false)
    .nullable(),
});

// Legacy schema for user credentials (separate from patient registration)
export const userCredentialsSchema = Yup.object().shape({
  userName: Yup.string()
    .min(2, "Too Short Name!")
    .max(50, "Too Long Name!")
    .required("User name is required."),
  password: Yup.string()
    .required("Password is required.")
    .max(18, "Password is too long - should be maximum 20 chars.")
    .min(7, "Password is too short - should be 8 chars minimum.")
    .matches(/[0-9]/, "Password requires a number")
    .matches(/[a-z]/, "Password requires a lowercase letter")
    .matches(/[A-Z]/, "Password requires an uppercase letter")
    .matches(/[^\w]/, "Password requires a special chars"),
  confirmPassword: Yup.string()
    .oneOf(
      [Yup.ref("password"), null],
      "Password and confirm password does not match"
    )
    .required("Confirm password is required."),
  MRNno: Yup.string().required("MRN Number is required."),
});

export const editPatientSchema = Yup.object().shape({
  // Personal Information - EMR Required Fields
  first_name: Yup.string()
    .min(2, validationMessages.firstName.min)
    .max(50, validationMessages.firstName.max)
    .matches(nameRegExp, validationMessages.firstName.matches)
    .required(validationMessages.firstName.required),
  
  last_name: Yup.string()
    .min(2, validationMessages.lastName.min)
    .max(50, validationMessages.lastName.max) 
    .matches(nameRegExp, validationMessages.lastName.matches)
    .required(validationMessages.lastName.required),
  
  middle_name: Yup.string()
    .max(50, "Middle name cannot exceed 50 characters")
    .matches(nameRegExp, "Middle name can only contain letters, spaces, apostrophes, hyphens, and dots")
    .nullable(),
  
  date_of_birth: Yup.date()
    .max(new Date(), "Date of birth cannot be in the future")
    .min(new Date(1900, 0, 1), "Please enter a valid date of birth")
    .required("Date of birth is required"),
  
  gender: Yup.string()
    .oneOf(['male', 'female', 'other'], 'Please select a valid gender')
    .required("Gender is required"),
  
  // EMR Required Demographic Fields
  religion: Yup.string()
    .oneOf(VALID_RELIGION_CODES, 'Please select a valid religion')
    .required("Religion is required"),
  
  race: Yup.string()
    .max(50, "Race cannot exceed 50 characters")
    .matches(nameRegExp, "Race can only contain letters, spaces, apostrophes, hyphens, and dots")
    .required("Race is required"),

  language: Yup.string()
    .oneOf(VALID_LANGUAGE_CODES, 'Please select a valid language')
    .required("Language is required"),
  
  marital_status: Yup.string()
    .oneOf(['single', 'married', 'divorced', 'widowed', 'separated'], 'Please select a valid marital status')
    .required("Marital status is required"),
  
  nationality: Yup.string()
    .max(100, 'Nationality must be less than 100 characters')
    .matches(nameRegExp, "Nationality can only contain letters, spaces, apostrophes, hyphens, and dots")
    .required("Nationality is required"),
  
  country: Yup.string()
    .max(100, "Country cannot exceed 100 characters")
    .required("Country of residence is required"),
  
  state: Yup.string()
    .oneOf(VALID_UAE_STATES, 'Please select a valid state')
    .required("State is required"),
  
  citizenship: Yup.string()
    .oneOf(['UAE', 'Resident', 'Visitor'], 'Please select a valid citizenship type')
    .required("Citizenship is required"),
  
  city: Yup.string()
    .max(100, "City cannot exceed 100 characters")
    .matches(nameRegExp, "City can only contain letters, spaces, apostrophes, hyphens, and dots")
    .required("City is required"),
  
  // Contact Information - EMR Required
  phone_country_code: Yup.string()
    .required("Country code is required"),
  
  phone_number: Yup.string()
    .matches(/^\d{7,15}$/, "Please enter a valid phone number")
    .required("Phone number is required"),
  
  permanent_address: Yup.string()
    .min(10, "Address must be at least 10 characters")
    .max(200, "Address cannot exceed 200 characters")
    .required("Permanent address is required"),
  
  // Medical Information - EMR Required
  allergy: Yup.string()
    .max(500, "Allergy information cannot exceed 500 characters")
    .required("Allergy information is required"),
  
  primary_physician: Yup.string()
    .max(200, "Primary physician name cannot exceed 200 characters")
    .matches(nameRegExp, "Name contains invalid characters")
    .required("Primary physician is required"),
  
  primary_physician_license: Yup.string()
    .max(50, "License number cannot exceed 50 characters")
    .required("Primary physician license is required"),
  
  // Identification - Flexible (either Emirates ID or Passport Number)
  emirates_id: Yup.string()
    .transform((value) => value ? value.replace(/[-\s]/g, '') : value) // Strip dashes and spaces for validation
    .trim()
    .test('emirates-format', validationMessages.emiratesId.matches, function(value) {
      return !value || emiratesIdRegExp.test(value);
    }),
  
  passport_number: Yup.string()
    .trim()
    .max(20, "Passport number cannot exceed 20 characters")
    .test('passport-format', validationMessages.passportNumber.matches, function(value) {
      return !value || passportRegExp.test(value.toUpperCase());
    }),
  
  insurance_provider: Yup.string()
    .max(100, "Insurance provider cannot exceed 100 characters")
    .nullable(),
  
  insurance_policy_number: Yup.string()
    .max(50, "Policy number cannot exceed 50 characters")
    .nullable(),
  
  insurance_subscriber_name: Yup.string()
    .max(100, "Subscriber name cannot exceed 100 characters")
    .matches(nameRegExp, "Subscriber name can only contain letters, spaces, apostrophes, hyphens, and dots")
    .nullable(),
  
  // Emergency Contact - Optional
  emergency_contact_name: Yup.string()
    .max(100, "Emergency contact name cannot exceed 100 characters")
    .matches(nameRegExp, "Emergency contact name can only contain letters, spaces, apostrophes, hyphens, and dots")
    .nullable(),
  
  emergency_contact_country_code: Yup.string()
    .nullable(),
  
  emergency_contact_phone: Yup.string()
    .matches(/^\d{7,15}$/, "Please enter a valid phone number")
    .nullable(),
  
  emergency_contact_relationship: Yup.string()
    .oneOf(VALID_RELATIONSHIPS, `Invalid relationship. Must be one of: ${VALID_RELATIONSHIPS.join(', ')}`)
    .nullable(),
  
  // Additional Medical fields
  blood_group: Yup.string()
    .oneOf(['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-', 'unknown'], 'Please select a valid blood group')
    .nullable(),
  
  height: Yup.number()
    .positive("Height must be a positive number")
    .max(300, "Height cannot exceed 300 cm")
    .nullable(),
  
  weight: Yup.number()
    .positive("Weight must be a positive number")
    .max(500, "Weight cannot exceed 500 kg")
    .nullable(),

  // Patient Classification Flags
  is_vip: Yup.boolean()
    .default(false)
    .nullable(),

  is_tourist: Yup.boolean()
    .default(false)
    .nullable(),

  // Legacy fields for backward compatibility
  phone_primary: Yup.string()
    .matches(phoneRegExp, validationMessages.phone.matches)
    .nullable(),
  
  phone_secondary: Yup.string()
    .matches(phoneRegExp, validationMessages.phone.matches)
    .nullable(),
  
  email: Yup.string()
    .email(validationMessages.email.email)
    .matches(emailRegExp, validationMessages.email.matches)
    .nullable(),
  
  street: Yup.string()
    .max(200, "Street address cannot exceed 200 characters")
    .nullable(),
  
  emirate: Yup.string()
    .max(100, "Emirate cannot exceed 100 characters")
    .nullable(),
  
  postal_code: Yup.string()
    .matches(postalCodeRegExp, "Please enter a valid postal code")
    .max(20, "Postal code cannot exceed 20 characters")
    .nullable(),
});

// Complete Patient Registration Schema
export const completeRegistrationSchema = Yup.object().shape({
  // Medical Information (required for complete registration)
  blood_group: Yup.string()
    .oneOf(['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'], 'Please select a valid blood group')
    .required("Blood group is required for complete registration"),
  
  height: Yup.number()
    .min(30, validationMessages.height.min)
    .max(300, validationMessages.height.max)
    .typeError(validationMessages.height.typeError)
    .required("Height is required for complete registration"),
  
  weight: Yup.number()
    .min(1, validationMessages.weight.min)
    .max(500, validationMessages.weight.max)
    .typeError(validationMessages.weight.typeError)
    .required("Weight is required for complete registration"),
  
  // Insurance Information
  insurance_provider: Yup.string()
    .max(100, "Insurance provider cannot exceed 100 characters")
    .nullable(),
  
  insurance_policy_number: Yup.string()
    .max(50, "Policy number cannot exceed 50 characters")
    .nullable(),
  
  insurance_subscriber_name: Yup.string()
    .max(100, "Subscriber name cannot exceed 100 characters")
    .matches(nameRegExp, "Subscriber name can only contain letters, spaces, apostrophes, hyphens, and dots")
    .nullable(),
  
  // Staff Assignment
  primary_physician_id: Yup.string()
    .nullable(),
  
  primary_nurse_id: Yup.string()
    .nullable(),
  
  // Medical History Arrays - validate as comma-separated strings
  known_allergies: Yup.array()
    .of(Yup.string().trim().min(1, "Allergy cannot be empty"))
    .nullable(),
  
  current_medications: Yup.array()
    .of(Yup.string().trim().min(1, "Medication cannot be empty"))
    .nullable(),
  
  medical_conditions: Yup.array()
    .of(Yup.string().trim().min(1, "Medical condition cannot be empty"))
    .nullable(),
  
  assigned_services: Yup.array()
    .of(Yup.string().trim().min(1, "Service cannot be empty"))
    .nullable(),

  // Patient Classification Flags
  is_vip: Yup.boolean()
    .default(false)
    .nullable(),

  is_tourist: Yup.boolean()
    .default(false)
    .nullable(),
});

// Export validation messages for use in components
export { validationMessages };